import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Event as EventIcon,
  Group as GroupIcon,
  TrendingUp as TrendingIcon,
  Assignment as ComplianceIcon,
  Business as CorporateIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import ClientSelection from '../components/ClientSelection.jsx';
import Layout from '../components/layout/Cslayout';

const Csdashboard = () => {
  const theme = useTheme();

  const documentStats = [
    { name: 'Jan', value: 65, target: 70 },
    { name: 'Feb', value: 59, target: 65 },
    { name: 'Mar', value: 80, target: 75 },
    { name: 'Apr', value: 81, target: 80 },
    { name: 'May', value: 56, target: 70 },
    { name: 'Jun', value: 55, target: 60 },
  ];

  const complianceData = [
    { name: 'Completed', value: 300, color: theme.palette.success.main },
    { name: 'Pending', value: 50, color: theme.palette.warning.main },
    { name: 'Overdue', value: 100, color: theme.palette.error.main },
  ];

  const meetingStats = [
    { name: 'Board Meetings', value: 12, required: 15 },
    { name: 'Committee Meetings', value: 19, required: 20 },
    { name: 'Shareholder Meetings', value: 3, required: 2 },
    { name: 'Special Meetings', value: 5, required: 4 },
  ];

  const StatCard = ({ title, value, subtitle, icon, color, trend, trendValue }) => (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "visible",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.6)})`,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 56,
              height: 56,
              border: `2px solid ${alpha(color, 0.2)}`,
            }}
          >
            {icon}
          </Avatar>
          {trend && (
            <Chip
              icon={trend === "up" ? <TrendingIcon /> : null}
              label={trendValue}
              size="small"
              sx={{
                bgcolor: trend === "up" 
                  ? alpha(theme.palette.success.main, 0.1) 
                  : alpha(theme.palette.error.main, 0.1),
                color: trend === "up" 
                  ? theme.palette.success.main 
                  : theme.palette.error.main,
                fontWeight: 600,
                border: "none",
              }}
            />
          )}
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            mb: subtitle ? 0.5 : 0,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: 'block',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, subtitle, children, height = 350 }) => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: height,
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ flex: 1 }}>
        {children}
      </Box>
    </Paper>
  );

  const ComplianceStatusCard = () => (
    <ChartCard 
      title="Compliance Overview" 
      subtitle="Current compliance status distribution"
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={complianceData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            dataKey="value"
            startAngle={90}
            endAngle={450}
          >
            {complianceData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value) => [value, 'Items']}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 8,
              fontFamily: theme.typography.fontFamily,
            }}
          />
          <Legend 
            wrapperStyle={{
              fontFamily: theme.typography.fontFamily,
              fontSize: '12px',
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );

  return (
    <Layout>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Company Secretary Dashboard
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Monitor corporate governance, compliance, and secretarial activities
          </Typography>
        </Box>

      
        {/* Key Metrics Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Documents"
              subtitle="Processed this month"
              value="1,234"
              icon={<DocumentIcon sx={{ fontSize: 28 }} />}
              color={theme.palette.info.main}
              trend="up"
              trendValue="+8.5%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Upcoming Meetings"
              subtitle="Next 30 days"
              value="8"
              icon={<EventIcon sx={{ fontSize: 28 }} />}
              color={theme.palette.success.main}
              trend="up"
              trendValue="+2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Directors"
              subtitle="Current board strength"
              value="12"
              icon={<GroupIcon sx={{ fontSize: 28 }} />}
              color={theme.palette.warning.main}
              trend="up"
              trendValue="+1"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Compliance Score"
              subtitle="Overall compliance rating"
              value="94%"
              icon={<TrendingIcon sx={{ fontSize: 28 }} />}
              color={theme.palette.primary.main}
              trend="up"
              trendValue="+2%"
            />
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={8}>
            <ChartCard
              title="Document Processing Trends"
              subtitle="Monthly document processing vs targets"
              height={400}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={documentStats}>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={alpha(theme.palette.text.secondary, 0.1)}
                  />
                  <XAxis 
                    dataKey="name" 
                    stroke={theme.palette.text.secondary}
                    style={{ fontFamily: theme.typography.fontFamily }}
                  />
                  <YAxis 
                    stroke={theme.palette.text.secondary}
                    style={{ fontFamily: theme.typography.fontFamily }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderRadius: 8,
                      fontFamily: theme.typography.fontFamily,
                    }}
                  />
                  <Legend 
                    wrapperStyle={{
                      fontFamily: theme.typography.fontFamily,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={theme.palette.primary.main}
                    strokeWidth={3}
                    dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 6 }}
                    name="Processed"
                  />
                  <Line
                    type="monotone"
                    dataKey="target"
                    stroke={alpha(theme.palette.text.secondary, 0.5)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    dot={{ fill: theme.palette.text.secondary, strokeWidth: 2, r: 4 }}
                    name="Target"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <ComplianceStatusCard />
          </Grid>
        </Grid>

        {/* Meeting Statistics */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard
              title="Meeting Statistics"
              subtitle="Current year meeting schedule and completion status"
              height={400}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={meetingStats} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={alpha(theme.palette.text.secondary, 0.1)}
                  />
                  <XAxis 
                    dataKey="name" 
                    stroke={theme.palette.text.secondary}
                    style={{ fontFamily: theme.typography.fontFamily }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke={theme.palette.text.secondary}
                    style={{ fontFamily: theme.typography.fontFamily }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderRadius: 8,
                      fontFamily: theme.typography.fontFamily,
                    }}
                  />
                  <Legend 
                    wrapperStyle={{
                      fontFamily: theme.typography.fontFamily,
                    }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                    name="Completed"
                  />
                  <Bar 
                    dataKey="required" 
                    fill={alpha(theme.palette.text.secondary, 0.3)}
                    radius={[4, 4, 0, 0]}
                    name="Required"
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>

        {/* Quick Actions or Additional Info Section */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main,
                      mr: 2,
                    }}
                  >
                    <CheckIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Recent Completions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      5 compliance items completed today
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main,
                      mr: 2,
                    }}
                  >
                    <ScheduleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Pending Actions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      3 items require immediate attention
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      mr: 2,
                    }}
                  >
                    <CorporateIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Corporate Updates
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      2 regulatory changes this week
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default Csdashboard;